/**
 * Shared presentation atoms for the Helpdesk inbox panes — the person avatar
 * (dicebear + initials fallback), the session-status pill, channel glyphs, and
 * the two time formats. Styles mirror the storybook `final/_helpdesk.tsx`
 * reference; data comes from the real handoff/session backend only.
 */
import { Avatar, AvatarFallback, AvatarImage, cn } from "@cometchat-ai/ui-dashboard";
import { useTranslation } from "react-i18next";

import { channelMeta } from "../agents/conversations/presentation";
import { convAccent, initials } from "../agents/conversations/presentation";
import { dicebearMicah } from "../lib/avatar";
import type { SessionStatus } from "../handoff/api";

/** Deterministic person avatar, rounded-square, tinted by the name. */
export function HdAvatar({
  name,
  src,
  size = 36,
}: {
  name: string;
  src?: string | null;
  size?: number;
}) {
  const accent = convAccent(name);
  const radius = Math.round(size * 0.28);
  return (
    <Avatar
      className="shrink-0 border"
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        borderColor: `color-mix(in oklch, ${accent} 16%, transparent)`,
      }}
    >
      <AvatarImage src={src || dicebearMicah(name)} alt="" style={{ borderRadius: radius }} />
      <AvatarFallback
        className="text-xs font-semibold"
        style={{
          borderRadius: radius,
          background: `color-mix(in oklch, ${accent} 8%, transparent)`,
          color: accent,
        }}
      >
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  );
}

const STATUS_HUE: Record<SessionStatus, string> = {
  queued: "var(--color-warning)",
  human_handling: "var(--color-info)",
  resolved: "var(--color-fg-muted)",
  ai_handling: "var(--color-success)",
};

/** Session-status chip — gray-tinted fill in the row's semantic hue. */
export function StatusPill({ status, className }: { status: SessionStatus; className?: string }) {
  const { t } = useTranslation("workspace");
  const hue = STATUS_HUE[status];
  return (
    <span
      className={cn(
        "inline-flex h-5 shrink-0 items-center rounded-full px-2 text-xxs font-medium",
        className,
      )}
      style={{ color: hue, background: `color-mix(in oklch, ${hue} 12%, transparent)` }}
    >
      {t(`handoff.status.${status}`)}
    </span>
  );
}

/** Channel glyph + label, from the shared conversations presentation map. */
export function ChannelPill({ channel }: { channel: string }) {
  const { label, icon: Icon } = channelMeta(channel);
  return (
    <span className="inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-muted px-2 text-xxs font-medium text-muted-foreground">
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {label}
    </span>
  );
}

/** "13 Jul, 14:03" — compact absolute stamp for list rows. */
export function fmtTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
}

/** "14:03" — clock only, for message bubbles. */
export function fmtClock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/**
 * Human label for a contact. Anonymous web/playground visitors carry no
 * displayName/email, so — like the workspace Conversations inbox — we fall back
 * to the session `title` (the conversation topic) before "Unknown contact", so
 * rows stay distinguishable instead of all reading "Unknown contact".
 */
export function contactName(
  contact: { displayName: string | null; email: string | null } | null,
  fallback?: string | null,
): string {
  return contact?.displayName ?? contact?.email ?? fallback ?? "Unknown contact";
}
